import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { SettingsProvider } from './context/SettingsContext';
import { SensorNamesProvider } from './context/SensorNamesContext';
import { SessionProvider } from './context/SessionContext';
import LoginForm from './components/Auth/LoginForm';
import Sidebar from './components/Layout/Sidebar';
import HomePage from './components/Home/HomePage';
import SoilOverview from './components/Soil/SoilOverview';
import LocationPage from './components/Locations/LocationPage';
import WeatherPage from './components/Weather/WeatherPage';
import PestPage from './components/Pest/PestPage';
import UsersPage from './components/Admin/UsersPage';
import SettingsPage from './components/Settings/SettingsPage';
import BatteryPage from './components/Battery/BatteryPage';
import AlertsPage from './components/Alerts/AlertsPage';
import ThresholdsPage from './components/Thresholds/ThresholdsPage';
import MapsPage from './components/Maps/MapsPage';
import AdminApp from './admin/AdminApp';
import { ALL_MODULE_KEYS, type ModuleKey } from './constants/modules';
import { wsManager } from './services/websocket';

// admin.dimasfarming.com: consola separada, ver apps/web/src/admin/AdminApp.tsx.
// Mismo patrón que VITE_SHARED_HOSTS en components/Auth/LoginForm.tsx.
const PLATFORM_HOSTS = (import.meta.env.VITE_PLATFORM_HOSTS ?? 'admin.dimasfarming.com')
  .split(',').map((h: string) => h.trim().toLowerCase()).filter(Boolean);
const isPlatformHost = PLATFORM_HOSTS.includes(window.location.hostname.toLowerCase());

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('jwt'));
  const [currentPage, setCurrentPage]         = useState('home');
  const [user, setUser]                       = useState<any>(null);
  const [isDark, setIsDark]                   = useState(() => localStorage.getItem('theme') === 'dark');

  // Aplicar dark mode al documento
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  useEffect(() => {
    const token = localStorage.getItem('jwt');
    if (token && isAuthenticated) {
      wsManager.connect(token);
      wsManager.on('connected', (data) => setUser(data));
      wsManager.on('telemetry', () => {});
    }
    return () => {};
  }, [isAuthenticated]);

  const handleLogout = () => {
    localStorage.removeItem('jwt');
    setIsAuthenticated(false);
    wsManager.disconnect();
    window.location.reload();
  };

  if (!isAuthenticated) {
    return <LoginForm onSuccess={() => setIsAuthenticated(true)} />;
  }

  // admin.dimasfarming.com: consola completamente separada, sin el
  // Sidebar/páginas operativas de abajo — ver apps/web/src/admin/AdminApp.tsx.
  if (isPlatformHost) {
    return (
      <AdminApp
        user={user}
        onLogout={handleLogout}
        onThemeToggle={() => setIsDark(d => !d)}
        isDark={isDark}
        onUserUpdated={(patch) => setUser((u: any) => ({ ...u, ...patch }))}
      />
    );
  }

  // enabledModules llega en el payload `connected` del WS (ver
  // websocket/routes.ts) — mientras no responda, todo visible por defecto
  // para no parpadear. Defensa extra además de ocultar el nav en Sidebar.tsx:
  // si currentPage quedó apuntando a un módulo ya deshabilitado (toggle a
  // mitad de sesión, estado stale), no se renderiza — cae a Home.
  const enabledModules = new Set<ModuleKey>(user?.enabledModules ?? ALL_MODULE_KEYS);
  const isToggleable = (ALL_MODULE_KEYS as readonly string[]).includes(currentPage);
  const pageBlocked = isToggleable && !enabledModules.has(currentPage as ModuleKey);

  return (
    <SessionProvider session={user}>
    <SettingsProvider>
    <SensorNamesProvider>
    <div className="app">
      <Sidebar
        currentPage={currentPage}
        onNavigate={setCurrentPage}
        user={user}
        onLogout={handleLogout}
        onThemeToggle={() => setIsDark(d => !d)}
        isDark={isDark}
      />
      <div className="content">
        {(currentPage === 'home' || pageBlocked) && <HomePage />}
        {currentPage === 'soil'       && enabledModules.has('soil')       && <SoilOverview />}
        {currentPage === 'weather'    && enabledModules.has('weather')    && <WeatherPage />}
        {currentPage === 'pest'       && enabledModules.has('pest')       && <PestPage />}
        {currentPage === 'battery'    && enabledModules.has('battery')    && <BatteryPage />}
        {currentPage === 'alerts'     && enabledModules.has('alerts')     && <AlertsPage />}
        {currentPage === 'thresholds' && enabledModules.has('thresholds') && <ThresholdsPage />}
        {currentPage === 'locations'  && enabledModules.has('locations')  && <LocationPage />}
        {currentPage === 'reports'    && enabledModules.has('reports')    && <ReportsPlaceholder />}
        {currentPage === 'mapas'      && enabledModules.has('mapas')      && <MapsPage userRole={user?.role ?? ''} />}
        {currentPage === 'users'    && <UsersPage userRole={user?.role ?? ''} />}
        {currentPage === 'settings' && (
          <SettingsPage
            user={user}
            onThemeToggle={() => setIsDark(d => !d)}
            isDark={isDark}
            onUserUpdated={(patch) => setUser((u: any) => ({ ...u, ...patch }))}
          />
        )}
      </div>
    </div>
    </SensorNamesProvider>
    </SettingsProvider>
    </SessionProvider>
  );
}

function ReportsPlaceholder() {
  const { t } = useTranslation();
  return (
    <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', maxWidth: 380 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--dark)', marginBottom: 8 }}>{t('reports.title')}</div>
        <div style={{ fontSize: 13, color: 'var(--gray)', lineHeight: 1.6, marginBottom: 24 }}>
          {t('reports.soon')}
        </div>
        <div style={{
          display: 'inline-block', fontSize: 11, fontWeight: 600,
          color: 'var(--amber)', background: 'var(--amber-bg)',
          border: '1px solid var(--amber)', borderRadius: 20,
          padding: '4px 14px',
        }}>
          {t('reports.underDevelopment')}
        </div>
      </div>
    </div>
  );
}
